import React from 'react';
import { useDocumentTitle } from '../../hooks/use-document-title';
import { usePortalLiveRefresh } from '../../hooks/use-portal-live-refresh';
import {
  ListSkeleton,
  LoadError,
} from '../dashboard/dashboard-shared-components/load-state';
import { listItemMotion, Staleable, StateSwitch } from '../../../motion';
import {
  TenantParcel,
  getTenantParcelsPage,
} from '../../../api/tenant-client';
import { StatusTone } from '../../status-pill/status-pill';
import { countOf } from '../../../function-utils/plural';
import {
  LIVE_PARCEL_EVENTS,
  ListMore,
  PAGE_SIZE,
  primaryHome,
  readFailure,
  rowZone,
  usePagedPanel,
  usePortalOverview,
  whenAt,
  zoneNote,
  zonesOf,
  zoneTag,
} from './portal-common';
import {
  Card,
  EmptyState,
  ListRow,
  RowMain,
  RowMeta,
  RowPill,
  RowTitle,
  TabLead,
  TabTitle,
} from './portal-kit.styled';

/**
 * PARCELS — everything the desk has logged for your unit, what is still on the
 * shelf first.
 *
 * The server's first page carries EVERY parcel still waiting, whatever the page
 * size, then the newest collected ones; "Show more" pages back through the
 * collected and returned ones only. So the actionable half is never behind a
 * button.
 */

const parcelLine = (parcel: TenantParcel): string => {
  const bits: string[] = [];
  bits.push(parcel.carrier ? `${parcel.carrier} delivery` : 'A delivery');
  if ((parcel.parcel_count || 1) > 1) bits.push(`${parcel.parcel_count} items`);
  if (parcel.tracking_last4) bits.push(`ending ${parcel.tracking_last4}`);

  return bits.join(' · ');
};

/**
 * WHAT HAPPENED TO THE PARCEL, IN THE RESIDENT'S WORDS, and its tone.
 *
 * A COLLECTION TIMESTAMP IS THE STRONGEST FACT ON THE ROW, so it is read first:
 * if the desk handed it over, it was collected, whatever the record has since
 * become — `redacted` (a collected parcel whose details were later erased) once
 * fell through to "Logged in error" for sixteen parcels people had collected.
 * Only a waiting parcel carries the warning tone; everything else has already
 * happened.
 */
const parcelState = (parcel: TenantParcel): { label: string; tone: StatusTone } => {
  if (parcel.picked_up_at) return { label: 'Collected', tone: 'success' };
  switch (parcel.status) {
    case 'awaiting_pickup':
      return { label: 'Waiting', tone: 'warning' };
    case 'picked_up':
      return { label: 'Collected', tone: 'success' };
    case 'returned':
      return { label: 'Returned', tone: 'neutral' };
    default:
      return { label: 'Logged in error', tone: 'neutral' };
  }
};

export const PortalParcels: React.FC = () => {
  useDocumentTitle('Your parcels');
  const { data: overview } = usePortalOverview();
  const tz = primaryHome(overview)?.building_timezone;
  const list = usePagedPanel(getTenantParcelsPage);
  /* A parcel logged or collected → the bell rings → this list re-reads. */
  usePortalLiveRefresh(LIVE_PARCEL_EVENTS, list.reload);

  const parcels = list.items;
  const waiting = parcels.filter(p => p.status === 'awaiting_pickup').length;
  /* EACH PARCEL IN ITS OWN BUILDING'S CLOCK (RP-9.9). With one zone on the
     list the note under it names it, as always; with two, every row names its
     own and the list-wide note would be wrong for half of them. */
  const zones = zonesOf(parcels, tz);
  const mixed = zones.length > 1;
  const zone = mixed ? '' : zoneNote(zones[0] || tz);

  /* "NOTHING ON THE SHELF" WAS PRINTED DIRECTLY ABOVE FOUR PARCELS. Both were
     true — none was waiting, all had been collected — but nobody reads a screen
     that way. The lead describes the list under it, not just the subset the
     Home headline is about. */
  const lead = waiting
    ? `${waiting} on the shelf at the front desk.`
    : list.total
      ? `Nothing waiting · ${countOf(list.total, 'parcel')} in all.`
      : 'Nothing on the shelf.';

  return (
    <>
      <TabTitle>Your parcels</TabTitle>
      <TabLead>
        {list.status === 'ready' ? lead : 'Parcels the desk has logged for your unit.'}
      </TabLead>

      <StateSwitch state={list.status} minHeight={360}>
        {list.status === 'loading' ? (
          <ListSkeleton rows={4} label="your parcels" />
        ) : list.status === 'error' ? (
          <LoadError
            message={readFailure(list.errorStatus, 'We could not load your parcels just now.')}
            onRetry={list.reload}
          />
        ) : (
          <Staleable stale={list.refreshing}>
            {list.failed ? (
              <LoadError
                message={readFailure(
                  list.errorStatus,
                  'We could not refresh your parcels just now. What is shown may be out of date.'
                )}
                onRetry={list.reload}
              />
            ) : null}
            <Card>
              {parcels.length === 0 ? (
                <EmptyState>
                  Nothing yet. Parcels logged for your unit appear here as soon
                  as the desk takes them in.
                </EmptyState>
              ) : (
                parcels.map((parcel, index) => {
                  const state = parcelState(parcel);
                  const at = rowZone(parcel, tz);

                  return (
                    <ListRow
                      key={parcel.id}
                      /* The cascade is per PAGE: a page appended by "Show more"
                         arrives from 0 rather than waiting out delays for rows
                         already on screen. No layout animation — the list only
                         grows. */
                      {...listItemMotion(index % PAGE_SIZE)}
                      layout={false}
                    >
                      <RowMain>
                        <RowTitle>{parcelLine(parcel)}</RowTitle>
                        <RowMeta>
                          {whenAt(parcel.logged_at, at)}
                          {parcel.status === 'awaiting_pickup' && parcel.shelf
                            ? ` · shelf ${parcel.shelf}`
                            : ''}
                          {parcel.picked_up_at
                            ? ` · collected ${whenAt(parcel.picked_up_at, at)}`
                            : ''}
                          {zoneTag(at, mixed)}
                        </RowMeta>
                      </RowMain>
                      <RowPill tone={state.tone}>{state.label}</RowPill>
                    </ListRow>
                  );
                })
              )}
            </Card>
            <ListMore
              shown={parcels.length}
              total={list.total}
              nextCursor={list.nextCursor}
              pages={list.pages}
              loadingMore={list.loadingMore}
              moreFailed={list.moreFailed}
              onMore={list.loadMore}
              noun="parcel"
              what="parcels"
              zone={zone}
            />
          </Staleable>
        )}
      </StateSwitch>
    </>
  );
};
